import { getSignupPage } from "../../../config/signupContent.js";

function escapeAttr(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}

function renderField(field) {
  if (field.type === "select") {
    const options = (field.options || [])
      .map((option) => {
        const selected = option.disabled ? " selected" : "";
        const disabled = option.disabled ? " disabled" : "";
        return `<option value="${escapeAttr(option.value)}"${selected}${disabled}>${option.label}</option>`;
      })
      .join("");

    return `
      <label class="signup-form__field contact__field">
        <span class="sr-only">${field.placeholder}</span>
        <select name="${field.name}" ${field.required ? "required" : ""} data-signup-field>
          ${options}
        </select>
      </label>
    `;
  }

  const minlength = field.minlength ? ` minlength="${field.minlength}"` : "";
  const autocomplete = field.autocomplete
    ? ` autocomplete="${escapeAttr(field.autocomplete)}"`
    : "";

  return `
    <label class="signup-form__field contact__field">
      <span class="sr-only">${field.placeholder}</span>
      <input
        type="${field.type}"
        name="${field.name}"
        placeholder="${escapeAttr(field.placeholder)}"
        ${field.required ? "required" : ""}
        ${minlength}
        ${autocomplete}
        data-signup-field
      />
    </label>
  `;
}

function renderTypeSelector(accountTypes, activeId) {
  return `
    <div class="signup-form__types" role="tablist" aria-label="Account type">
      ${accountTypes
        .map(
          (type) => `
        <button
          type="button"
          class="signup-form__type${type.id === activeId ? " is-active" : ""}"
          role="tab"
          aria-selected="${type.id === activeId ? "true" : "false"}"
          data-account-type="${type.id}"
        >
          <span class="signup-form__type-label">${type.label}</span>
          <span class="signup-form__type-desc">${type.description}</span>
        </button>
      `
        )
        .join("")}
    </div>
  `;
}

function renderSuccess(success) {
  return `
    <div class="signup-form__success" role="status">
      <p class="signup-form__success-kicker">${success.kicker || "Thank you"}</p>
      <h3 class="signup-form__success-title">${success.title}</h3>
      <p class="signup-form__success-body">${success.body}</p>
      <div class="signup-form__success-actions">
        <a class="btn btn--primary" href="${success.primaryHref}">${success.primaryLabel}</a>
        <a class="btn btn--ghost" href="${success.secondaryHref}">${success.secondaryLabel}</a>
      </div>
    </div>
  `;
}

export function createSignupFormSection() {
  const page = getSignupPage();
  const { form, success } = page;
  const defaultType = form.accountTypes[0]?.id || "client";

  const section = document.createElement("section");
  section.id = "register";
  section.className = "section signup-form-section";

  section.innerHTML = `
    <div class="section__inner signup-form-section__inner reveal">
      <div class="signup-form__panel" data-signup-panel>
        <div class="signup-form__intro">
          <p class="signup-form__eyebrow">Registration</p>
          <h2 class="signup-form__heading">Pick your account type</h2>
        </div>
        ${renderTypeSelector(form.accountTypes, defaultType)}
        <form class="signup-form contact__form" novalidate data-signup-form>
          <input type="hidden" name="accountType" value="${defaultType}" data-account-type-input />
          <div class="signup-form__shared" data-shared-fields>
            ${form.sharedFields.map(renderField).join("")}
          </div>
          <div class="signup-form__typed" data-typed-fields>
            ${(form.typeFields[defaultType] || []).map(renderField).join("")}
          </div>
          <p class="signup-form__error" data-signup-error hidden></p>
          <button class="btn btn--primary contact__submit" type="submit">${form.submitLabel}</button>
        </form>
      </div>
    </div>
  `;

  bindSignupForm(section, form, success);
  return section;
}

function readSharedValues(formEl) {
  const values = {};
  formEl.querySelectorAll("[data-shared-fields] [data-signup-field]").forEach((el) => {
    if (el.name) values[el.name] = el.value;
  });
  return values;
}

function applySharedValues(formEl, values) {
  Object.entries(values).forEach(([name, value]) => {
    const el = formEl.querySelector(`[data-shared-fields] [name="${name}"]`);
    if (el) el.value = value;
  });
}

function bindSelectFillState(root) {
  root.querySelectorAll("select[data-signup-field]").forEach((select) => {
    const sync = () => {
      select.classList.toggle("contact__select--filled", select.value !== "");
    };
    select.addEventListener("change", sync);
    sync();
  });
}

function bindSignupForm(section, form, success) {
  const panel = section.querySelector("[data-signup-panel]");
  const formEl = section.querySelector("[data-signup-form]");
  const typedWrap = section.querySelector("[data-typed-fields]");
  const typeInput = section.querySelector("[data-account-type-input]");
  const errorEl = section.querySelector("[data-signup-error]");
  const typeButtons = [...section.querySelectorAll("[data-account-type]")];

  let activeType = typeInput?.value || "client";

  const setType = (nextType) => {
    if (!form.typeFields[nextType]) return;

    if (nextType !== activeType) {
      const shared = readSharedValues(formEl);
      activeType = nextType;
      typedWrap.innerHTML = form.typeFields[nextType].map(renderField).join("");
      applySharedValues(formEl, shared);
      bindSelectFillState(typedWrap);
    } else {
      activeType = nextType;
    }

    if (typeInput) typeInput.value = activeType;

    typeButtons.forEach((btn) => {
      const on = btn.getAttribute("data-account-type") === activeType;
      btn.classList.toggle("is-active", on);
      btn.setAttribute("aria-selected", on ? "true" : "false");
    });
  };

  typeButtons.forEach((btn) => {
    btn.addEventListener("click", () => {
      setType(btn.getAttribute("data-account-type"));
    });
  });

  bindSelectFillState(formEl);

  formEl.addEventListener("submit", (event) => {
    event.preventDefault();
    errorEl.hidden = true;
    errorEl.textContent = "";

    if (!formEl.checkValidity()) {
      formEl.reportValidity();
      return;
    }

    const data = new FormData(formEl);
    const password = String(data.get("password") || "");
    const confirm = String(data.get("confirmPassword") || "");

    if (password !== confirm) {
      errorEl.hidden = false;
      errorEl.textContent = "Passwords do not match.";
      formEl.querySelector('[name="confirmPassword"]')?.focus();
      return;
    }

    if (password.length < 8) {
      errorEl.hidden = false;
      errorEl.textContent = "Password must be at least 8 characters.";
      return;
    }

    panel.innerHTML = renderSuccess(success);
  });
}
